import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import DramaCard from '@/components/feature/DramaCard';
import CategoryNav from '@/components/feature/CategoryNav';
import { getHotDramas, getLatestDramas, dramas, featuredDramas, categories } from '@/mocks/dramas';

export default function Home() {
  const hotDramas = getHotDramas();
  const latestDramas = getLatestDramas();
  const trendingDramas = dramas.filter((d) => d.rating >= 8.5).slice(0, 6);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden" aria-label="首页横幅">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20cozy%20bedroom%20at%20night%20with%20large%20TV%20screen%20glowing%20warm%20light%2C%20cinematic%20atmosphere%2C%20soft%20blankets%20on%20bed%2C%20warm%20amber%20and%20golden%20tones%2C%20movie%20night%20aesthetic%2C%20cozy%20vibes%2C%20cinematic%20photography%2C%20atmospheric%20lighting%2C%20comfortable%20home%20theater%20setup&width=1800&height=900&seq=hero-banner-beiwo&orientation=landscape"
            alt="被窝影院 - 免费电视剧大全"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 via-background-50/30 to-background-50" />
          <div className="relative z-10 w-full px-4 md:px-6 flex flex-col items-center text-center pt-20">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/90 backdrop-blur-sm text-background-50 text-sm font-medium mb-6">
              <i className="ri-fire-line text-base"></i>
              <span className="whitespace-nowrap">每日更新 · 极速播放 · 高清不缓冲</span>
            </div>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 mb-4 max-w-3xl leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              被窝里的<span className="text-primary-500">追剧天堂</span>
            </h2>
            <p className="text-foreground-600 text-base md:text-lg max-w-2xl mb-8 leading-relaxed">
              汇聚免费电视剧大全，全集在线看最新电视剧免费、热播剧完整版和国产剧免费观看，高清流畅在线不缓冲，你的追剧首选平台。
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="relative w-80 sm:w-96">
                <input
                  type="text"
                  placeholder="搜索你想看的电视剧..."
                  className="w-full h-12 pl-5 pr-12 rounded-full bg-background-100/90 backdrop-blur-sm border border-background-200/60 text-sm text-foreground-700 placeholder-foreground-500/60 focus:outline-none focus:border-primary-500/50 transition-colors"
                  aria-label="搜索电视剧"
                />
                <button className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 transition-colors cursor-pointer" aria-label="搜索">
                  <i className="ri-search-line"></i>
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
              <span className="text-foreground-500 text-xs whitespace-nowrap">大家都在搜：</span>
              {['狂飙', '三体', '漫长的季节', '繁花', '长安十二时辰', '庆余年'].map((term) => (
                <span
                  key={term}
                  className="px-3 py-1 rounded-full bg-background-100/90 backdrop-blur-sm border border-background-200/40 text-foreground-600 text-xs hover:border-primary-500/40 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {term}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-8 md:py-10" aria-label="分类导航">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 mb-5">
              <i className="ri-menu-2-line text-primary-500 text-lg"></i>
              <h2 className="text-lg md:text-xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                分类浏览
              </h2>
            </div>
            <CategoryNav />
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="py-10 md:py-14" aria-label="热门推荐">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <i className="ri-fire-line text-accent-500 text-xl"></i>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  热门推荐
                </h2>
              </div>
              <Link
                to="/category/国产剧"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-primary-500 text-sm hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                查看更多 <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {hotDramas.slice(0, 6).map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
          </div>
        </section>

        {/* Trending Now */}
        <section className="py-10 md:py-14 bg-background-100/50" aria-label="正在热播">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <i className="ri-flashlight-line text-primary-500 text-xl"></i>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  正在热播
                </h2>
              </div>
              <Link
                to="/category/国产剧"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-primary-500 text-sm hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                查看更多 <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {trendingDramas.map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-10 md:py-14" aria-label="最新上线">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <i className="ri-timer-line text-accent-500 text-xl"></i>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  最新更新
                </h2>
              </div>
              <Link
                to="/category/动漫"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-primary-500 text-sm hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                查看更多 <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {latestDramas.map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
              {dramas.filter((d) => d.status === '已完结').slice(0, 2).map((drama) => {
                const uniqueKey = `complete-${drama.id}`;
                return <DramaCard key={uniqueKey} drama={drama} />;
              })}
            </div>
          </div>
        </section>

        {/* Featured Section */}
        <section className="py-10 md:py-14 bg-background-100/50" aria-label="高分神作">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 mb-6">
              <i className="ri-star-line text-primary-500 text-xl"></i>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                高分神作
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {featuredDramas.slice(0, 4).map((drama) => (
                <Link
                  key={drama.id}
                  to={`/drama/${drama.id}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="group flex gap-4 p-4 rounded-lg bg-background-50 border border-background-200/50 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-20 h-28 flex-shrink-0 rounded-md overflow-hidden">
                    <img
                      src={drama.posterSrc}
                      alt={`${drama.title} 在线观看`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex flex-col justify-center min-w-0">
                    <h3 className="text-foreground-900 font-semibold text-sm truncate">{drama.title}</h3>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-accent-500 text-xs font-bold">{drama.rating}</span>
                      <span className="text-foreground-500 text-xs">/ 10</span>
                    </div>
                    <p className="text-foreground-500 text-xs mt-1">{drama.category} · {drama.year}</p>
                    <p className="text-foreground-400 text-xs mt-2 line-clamp-2">{drama.description}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {drama.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded-md bg-accent-100/80 text-accent-700 text-xs whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 md:py-16 relative overflow-hidden" aria-label="平台数据">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 via-accent-500/10 to-primary-500/10" />
          <div className="relative max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {[
                { icon: 'ri-movie-2-line', value: '10,000+', label: '海量剧集' },
                { icon: 'ri-user-smile-line', value: '500万+', label: '活跃用户' },
                { icon: 'ri-play-circle-line', value: '4K', label: '高清画质' },
                { icon: 'ri-speed-up-line', value: '0缓冲', label: '极速播放' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto rounded-full bg-background-50 border border-background-200/50 flex items-center justify-center mb-3">
                    <i className={`${stat.icon} text-primary-500 text-2xl`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-900 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                    {stat.value}
                  </div>
                  <div className="text-foreground-500 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 md:py-20" aria-label="开始追剧">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              今晚，在被窝里<span className="text-primary-500">开始追剧</span>
            </h2>
            <p className="text-foreground-600 mb-8 text-base leading-relaxed">
              无论你喜欢国产剧的史诗叙事、韩剧的细腻情感、美剧的高能剧情还是日漫的热血冒险，被窝影院都为你准备好了。免费电视剧大全，随时随地，全集在线看。
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {categories.slice(0, 4).map((cat) => (
                <Link
                  key={cat}
                  to={`/category/${encodeURIComponent(cat)}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  浏览{cat}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 md:py-16 bg-background-100/50" aria-label="常见问题">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                常见问题
              </h2>
              <p className="text-foreground-500 text-sm">关于被窝影院的常见问题解答</p>
            </div>
            <div className="space-y-3">
              {[
                { q: '被窝影院的电视剧是免费的吗？', a: '是的，被窝影院上的所有电视剧均为免费观看，无需会员或付费订阅。我们致力于为追剧爱好者提供免费电视剧大全，让每个人都能全集在线看最新电视剧免费内容。' },
                { q: '需要下载APP才能观看吗？', a: '不需要。被窝影院支持网页直接观看，打开浏览器即可在线看热播剧完整版。高清流畅在线不缓冲，随时随地开始追剧。' },
                { q: '国产剧免费观看的画质如何？', a: '被窝影院提供最高4K超高清画质，国产剧免费观看体验清晰细腻。同时我们采用先进的视频加速技术，确保在线看高清流畅不缓冲。' },
                { q: '更新频率是怎样的？', a: '被窝影院会在电视剧更新后第一时间同步上线，热播剧完整版通常在播出后数小时内即可观看。每日持续更新，让你永远不错过精彩剧情。' },
                { q: '有哪些类型的剧集可以观看？', a: '被窝影院提供丰富的剧集类型，包括国产剧、美剧、韩剧、日剧、动漫、综艺、纪录片和港剧等多种分类，满足不同观众的追剧需求。' },
              ].map((faq, idx) => (
                <details key={idx} className="group rounded-lg bg-background-50 border border-background-200/50 overflow-hidden">
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                    <h3 className="text-foreground-800 font-medium text-sm md:text-base pr-4">{faq.q}</h3>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-foreground-500 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Schema.org Structured Data */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "被窝影院 - 免费电视剧大全、全集在线看",
            "description": "被窝影院汇聚免费电视剧大全，全集在线看最新电视剧免费，热播剧完整版和国产剧免费观看，高清流畅在线不缓冲，追剧首选平台。",
            "url": "https://www.beiwoyingyuan.com/",
            "isPartOf": {
              "@type": "WebSite",
              "name": "被窝影院",
              "url": "https://www.beiwoyingyuan.com/"
            }
          })
        }} />
      </main>

      <Footer />
    </div>
  );
}